import React, { forwardRef, useImperativeHandle, useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import {
  EmbeddedField,
  ErrorText,
  FormColumn,
  FormContainer,
  FormGroup,
  FormInput,
  FormLabel,
  FormRow,
} from './gatewayFieldStyles';

// ===== GLOBAL STATE MANAGEMENT =====
let _collectJSLoadPromise = null;
let _collectJSConfigPromise = null;
let _collectJSConfiguredCallbacks = [];
let _collectJSConfigStartTime = null;

const CONFIG_TIMEOUT_MS = 15000;

/**
 * Reset all global CollectJS state
 */
function resetCollectJSState() {
  _collectJSConfigPromise = null;
  _collectJSConfiguredCallbacks = [];
  _collectJSConfigStartTime = null;
  window.__nmi_fields_ready = false;
}

/**
 * Wait for DOM containers to be present before configuring CollectJS
 */
function waitForDomContainers(selectors, timeoutMs = 5000) {
  return new Promise((resolve, reject) => {
    const startTime = Date.now();

    function check() {
      const allPresent = selectors.every(sel => document.querySelector(sel) !== null);

      if (allPresent) {
        resolve();
      } else if (Date.now() - startTime > timeoutMs) {
        const missing = selectors.filter(sel => document.querySelector(sel) === null);
        reject(new Error(`DOM containers not ready: ${missing.join(', ')}`));
      } else {
        requestAnimationFrame(check);
      }
    }

    setTimeout(check, 50);
  });
}

/**
 * Load a script with retry logic for transient network errors
 */
function buildCollectScriptCandidates(primarySrc) {
  const preferred = primarySrc || 'https://secure.networkmerchants.com/token/Collect.js';
  const candidates = [
    preferred,
    'https://sandbox.nmi.com/token/Collect.js',
    'https://secure.networkmerchants.com/token/Collect.js',
    'https://secure.networkmerchants.com/collect/js/collect.js',
  ];
  // Keep order stable while deduplicating.
  return candidates.filter((url, idx) => candidates.indexOf(url) === idx);
}

function loadScriptWithRetry(src, tokenizationKey, maxRetries = 3) {
  return new Promise((resolve, reject) => {
    let attempt = 0;
    const scriptSources = buildCollectScriptCandidates(src);

    function tryLoad(sourceIndex = 0) {
      attempt++;
      const scriptSrc = scriptSources[sourceIndex] || scriptSources[0];

      // Remove any existing scripts first
      const existingScripts = document.querySelectorAll('script[src*="Collect.js"], script[src*="collect.js"]');
      existingScripts.forEach((script) => {
        script.parentNode?.removeChild(script);
      });

      // Reset state
      delete window.CollectJS;
      delete window.__nmi_collect_configured;

      // Create fresh script
      const script = document.createElement('script');
      script.src = scriptSrc;
      script.async = true;
      script.setAttribute('data-tokenization-key', tokenizationKey);
      script.setAttribute('data-variant', 'inline');

      script.onload = () => {
        let pollAttempts = 0;
        const maxPollAttempts = 50;

        const poll = setInterval(() => {
          pollAttempts++;

          if (window.CollectJS && typeof window.CollectJS.configure === 'function') {
            clearInterval(poll);
            resolve(window.CollectJS);
          } else if (pollAttempts >= maxPollAttempts) {
            clearInterval(poll);
            reject(new Error('Collect.js loaded but CollectJS object not available'));
          }
        }, 100);
      };

      script.onerror = (e) => {
        script.parentNode?.removeChild(script);

        // First try alternative official Collect.js hosts/paths, then retry.
        if (sourceIndex < scriptSources.length - 1) {
          setTimeout(() => tryLoad(sourceIndex + 1), 250);
          return;
        }

        if (attempt < maxRetries) {
          const delay = Math.pow(2, attempt - 1) * 1000;
          setTimeout(() => tryLoad(0), delay);
        } else {
          reject(new Error('Failed to load Collect.js after ' + maxRetries + ' attempts'));
        }
      };

      document.head.appendChild(script);
    }

    tryLoad();
  });
}

function loadCollectJS(src, tokenizationKey) {
  if (window.CollectJS && typeof window.CollectJS.configure === 'function') {
    return Promise.resolve(window.CollectJS);
  }

  if (_collectJSLoadPromise) {
    return _collectJSLoadPromise;
  }

  _collectJSLoadPromise = loadScriptWithRetry(src, tokenizationKey, 3)
    .catch((err) => {
      _collectJSLoadPromise = null;
      throw err;
    });

  return _collectJSLoadPromise;
}

// ===== STYLED COMPONENTS =====
const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

const NmiElementContainer = styled(EmbeddedField)`
  & iframe {
    display: block !important;
    width: 100% !important;
    min-height: 22px !important;
    border: 0 !important;
    background: transparent !important;
  }
`;

const ShimmerPlaceholder = styled.div`
  position: absolute;
  top: 50%;
  left: 12px;
  right: 12px;
  transform: translateY(-50%);
  height: 16px;
  background: linear-gradient(
    90deg,
    #f0f0f0 0px,
    #e0e0e0 40px,
    #f0f0f0 80px
  );
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
  border-radius: 4px;
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  gap: var(--zyvgo-spacing-sm, 8px);
  color: var(--zyvgo-text-secondary, #666666);
  font-size: var(--zyvgo-small-size, 0.875rem);
`;

const Spinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid var(--zyvgo-border, #e1e5e9);
  border-top-color: var(--zyvgo-text-primary, #222222);
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const NmiCollectForm = forwardRef((props, ref) => {
  const { gatewayConfig } = props || {};
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [configured, setConfigured] = useState(false);
  const [mountedIframes, setMountedIframes] = useState(false);
  const [cardholderName, setCardholderName] = useState('');
  const [billingZip, setBillingZip] = useState('');
  const pendingResolveRef = useRef(null);
  const configuredRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    setLoading(true);
    setError(null);

    async function init() {
      const clientKey = gatewayConfig?.nmi_client_key || process.env.REACT_APP_NMI_CLIENT_KEY || null;

      if (!clientKey) {
        if (mountedRef.current) {
          setError('NMI configuration missing client-side tokenization key');
          setLoading(false);
        }
        return;
      }

      try {
        const scriptUrl =
          gatewayConfig?.nmi_script_url ||
          process.env.REACT_APP_NMI_SCRIPT_URL ||
          'https://secure.networkmerchants.com/token/Collect.js';

        await loadCollectJS(scriptUrl, clientKey);

        // Ensure hidden submit element exists
        if (!document.getElementById('__nmi_hidden_submit')) {
          const hidden = document.createElement('button');
          hidden.id = '__nmi_hidden_submit';
          hidden.style.display = 'none';
          document.body.appendChild(hidden);
        }

        // Inject stylesheet for CollectJS style sniffer
        if (!document.getElementById('__nmi_style_sniffer')) {
          const style = document.createElement('style');
          style.id = '__nmi_style_sniffer';
          style.type = 'text/css';
          style.appendChild(document.createTextNode(
            ".nmi-style-sniffer{display:block;width:100%;height:100%;padding:0;margin:0;border:none;box-sizing:border-box;background:transparent;color:inherit;font-size:inherit;line-height:inherit;border-radius:inherit;} .nmi-style-sniffer::placeholder{color:var(--zyvgo-text-placeholder,#aab7c4);opacity:1;}"
          ));
          document.head.appendChild(style);
        }

        if (window.CollectJS && typeof window.CollectJS.configure === 'function') {
          // Check for stale configuration
          if (_collectJSConfigPromise && _collectJSConfigStartTime) {
            const elapsed = Date.now() - _collectJSConfigStartTime;
            if (elapsed > CONFIG_TIMEOUT_MS) {
              resetCollectJSState();
            }
          }

          // Handle concurrent configuration attempts
          if (_collectJSConfigPromise) {
            try {
              const waitPromise = Promise.race([
                _collectJSConfigPromise,
                new Promise((_, reject) =>
                  setTimeout(() => reject(new Error('Config wait timeout')), CONFIG_TIMEOUT_MS)
                )
              ]);

              await waitPromise;

              if (mountedRef.current) {
                if (window.__nmi_fields_ready) {
                  setConfigured(true);
                  configuredRef.current = true;
                } else {
                  _collectJSConfiguredCallbacks.push(() => {
                    if (mountedRef.current) {
                      setConfigured(true);
                      configuredRef.current = true;
                    }
                  });
                }
              }
            } catch (e) {
              resetCollectJSState();
            }

            if (window.__nmi_fields_ready) {
              return;
            }
          }

          // Start configuration
          let configResolve;
          let configReject;
          _collectJSConfigPromise = new Promise((resolve, reject) => {
            configResolve = resolve;
            configReject = reject;
          });
          // The promise is also rejected when this form is replaced (for
          // example, when a patient switches from a saved card to a new one).
          // Attach a handler so that an expected lifecycle cancellation cannot
          // surface as an uncaught browser runtime error.
          _collectJSConfigPromise.catch(() => {});
          _collectJSConfigStartTime = Date.now();

          try {
            const fieldSelectors = ['#nmi-ccnumber', '#nmi-ccexp', '#nmi-cvv'];
            await waitForDomContainers(fieldSelectors);

            if (!mountedRef.current) {
              _collectJSConfigPromise = null;
              // This is an expected lifecycle race, not a user-visible
              // configuration failure. Resolve the shared wait quietly.
              configResolve();
              return;
            }

            // Clear previous iframes
            const containers = document.querySelectorAll('#nmi-ccnumber iframe, #nmi-ccexp iframe, #nmi-cvv iframe');
            containers.forEach(iframe => iframe.remove());
            setMountedIframes(false);

            // Clear previous CollectJS state
            if (typeof window.CollectJS.clearInputs === 'function') {
              try {
                window.CollectJS.clearInputs();
              } catch (e) {
                // Ignore clearInputs errors
              }
            }

            window.CollectJS.configure({
              variant: 'inline',
              paymentSelector: '#__nmi_hidden_submit',
              styleSniffer: true,
              snifferClass: 'nmi-style-sniffer',
              customCss: {
                'background-color': 'transparent',
                border: 'none',
                color: '#101828',
                'font-family': 'Arial, sans-serif',
                'font-size': '15px',
                'line-height': '22px',
                margin: '0',
                padding: '0',
              },
              placeholderCss: {
                color: '#98a2b3',
                'font-size': '15px',
              },
              focusCss: {
                color: '#101828',
              },
              invalidCss: {
                color: '#b42318',
              },
              fields: {
                ccnumber: {
                  selector: '#nmi-ccnumber',
                  title: 'Card Number',
                  placeholder: '1234 1234 1234 1234',
                },
                ccexp: {
                  selector: '#nmi-ccexp',
                  title: 'MM / YY',
                  placeholder: 'MM / YY',
                },
                cvv: {
                  selector: '#nmi-cvv',
                  title: 'CVC',
                  placeholder: 'CVC',
                },
              },
              fieldsAvailableCallback: function () {
                window.__nmi_fields_ready = true;

                _collectJSConfiguredCallbacks.forEach(cb => {
                  try { cb(); } catch (e) { /* ignore */ }
                });
                _collectJSConfiguredCallbacks = [];

                if (mountedRef.current) {
                  setMountedIframes(true);
                  setConfigured(true);
                  configuredRef.current = true;
                }
                configResolve();
              },
              callback: function (response) {
                try {
                  if (pendingResolveRef.current) {
                    if (response && response.token) {
                      pendingResolveRef.current({
                        payment_token: response.token,
                        card_meta: {
                          token: response.token,
                          card: response?.card || null,
                          card_type: response?.card?.type || response?.cardType || response?.type || '',
                          masked_cc: response?.card?.number || response?.masked || response?.masked_cc || '',
                          expiration: response?.card?.exp || response?.exp || response?.expiration || '',
                        },
                        normalized: { token_type: 'payment_token', token: response.token }
                      });
                    } else {
                      pendingResolveRef.current(Promise.reject(new Error('NMI token callback error')));
                    }
                    pendingResolveRef.current = null;
                  }
                } catch (e) {
                  if (pendingResolveRef.current) {
                    pendingResolveRef.current(Promise.reject(e));
                    pendingResolveRef.current = null;
                  }
                }
              },
              validationCallback: function (field, status, message) {
                // Validation callback - can be used for field-level validation feedback
              },
              timeoutCallback: function () {
                if (typeof configReject === 'function') {
                  configReject(new Error('CollectJS timeout'));
                }
              },
            });

            // Fallback: in some Collect.js versions the ready callback is flaky.
            // If fields are mounted, mark as ready to prevent blank oversized placeholders.
            setTimeout(() => {
              const mountedIframes = document.querySelectorAll('#nmi-ccnumber iframe, #nmi-ccexp iframe, #nmi-cvv iframe');
              if (!window.__nmi_fields_ready && mountedIframes.length >= 3) {
                window.__nmi_fields_ready = true;
                setMountedIframes(true);
                if (mountedRef.current) {
                  setConfigured(true);
                  configuredRef.current = true;
                }
                configResolve();
              }
            }, 1200);
          } catch (e) {
            _collectJSConfigPromise = null;
            configReject(e);
          }
        }
      } catch (err) {
        if (mountedRef.current) setError(err.message || String(err));
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    }

    init();

    return () => {
      mountedRef.current = false;
      if (pendingResolveRef.current) {
        try {
          pendingResolveRef.current(Promise.reject(new Error('Component unmounted before token received')));
        } catch (e) {
          // Swallow error
        }
        pendingResolveRef.current = null;
      }
      // Collect.js keeps global configuration and iframe state. Reset it when
      // this form disappears so a later edit/new-card form gets fresh DOM
      // containers instead of trying to reuse removed iframes.
      document
        .querySelectorAll('#nmi-ccnumber iframe, #nmi-ccexp iframe, #nmi-cvv iframe')
        .forEach((iframe) => iframe.remove());
      resetCollectJSState();
    };
  }, [gatewayConfig]);

  useEffect(() => {
    const poll = setInterval(() => {
      const count = document.querySelectorAll('#nmi-ccnumber iframe, #nmi-ccexp iframe, #nmi-cvv iframe').length;
      if (count >= 3) {
        setMountedIframes(true);
        clearInterval(poll);
      }
    }, 150);

    return () => clearInterval(poll);
  }, []);

  useImperativeHandle(ref, () => ({
    async getPaymentData() {
      setError(null);

      if (!cardholderName.trim()) {
        throw new Error('Cardholder name is required');
      }

      const normalizedZip = billingZip.trim().replace(/\D/g, '');
      if (!normalizedZip) {
        throw new Error('Billing ZIP code is required');
      }
      if (!/^\d{5}(\d{4})?$/.test(normalizedZip)) {
        throw new Error('Please enter a valid ZIP code');
      }

      if (configuredRef.current && window.CollectJS && typeof window.CollectJS.startPaymentRequest === 'function') {
        return await new Promise((resolve, reject) => {
          if (pendingResolveRef.current) {
            return reject(new Error('A tokenization request is already in progress'));
          }

          let settled = false;
          pendingResolveRef.current = (val) => {
            if (!settled) {
              settled = true;
              resolve(val);
            }
            pendingResolveRef.current = null;
          };

          try {
            window.CollectJS.startPaymentRequest();
          } catch (err) {
            pendingResolveRef.current = null;
            return reject(err);
          }

          const timeoutMs = 15000;
          const to = setTimeout(() => {
            if (!settled) {
              settled = true;
              if (pendingResolveRef.current) {
                pendingResolveRef.current = null;
              }
              reject(new Error('Timed out waiting for NMI token response'));
            }
          }, timeoutMs);

          const originalResolver = pendingResolveRef.current;
          pendingResolveRef.current = (val) => {
            clearTimeout(to);
            if (!settled) {
              settled = true;
              if (val && typeof val.then === 'function') {
                resolve(val);
              } else {
                resolve({
                  ...val,
                  cardholder_name: cardholderName.trim(),
                  postal_code: normalizedZip,
                });
              }
            }
            pendingResolveRef.current = null;
          };
        });
      }

      throw new Error('NMI Collect is not available. Please try another payment method or contact support.');
    },
  }));

  const showShimmer = loading || (!configured && !error && !mountedIframes);

  return (
    <FormContainer>
      <FormGroup>
        <FormLabel htmlFor="cardholder-name">
          Cardholder Name
        </FormLabel>
        <FormInput
          type="text"
          id="cardholder-name"
          placeholder="John Doe"
          value={cardholderName}
          onChange={(e) => setCardholderName(e.target.value)}
        />
      </FormGroup>

      <FormGroup>
        <FormLabel htmlFor="card-number-main">Card Number</FormLabel>
        <NmiElementContainer id="nmi-ccnumber">
          {showShimmer && <ShimmerPlaceholder />}
        </NmiElementContainer>
      </FormGroup>

      <FormRow>
        <FormColumn>
          <FormLabel htmlFor="expiration-date">Expiration Date</FormLabel>
          <NmiElementContainer id="nmi-ccexp">
            {showShimmer && <ShimmerPlaceholder />}
          </NmiElementContainer>
        </FormColumn>

        <FormColumn>
          <FormLabel htmlFor="cvc">CVC</FormLabel>
          <NmiElementContainer id="nmi-cvv">
            {showShimmer && <ShimmerPlaceholder />}
          </NmiElementContainer>
        </FormColumn>
      </FormRow>

      <FormGroup>
        <FormLabel htmlFor="billing-zip">Billing ZIP Code</FormLabel>
        <FormInput
          type="text"
          id="billing-zip"
          placeholder="12345"
          value={billingZip}
          onChange={(e) => setBillingZip(e.target.value)}
          inputMode="numeric"
        />
      </FormGroup>

      {loading && (
        <LoadingContainer>
          <Spinner />
          <span>Loading payment form...</span>
        </LoadingContainer>
      )}

      {error && <ErrorText>{error}</ErrorText>}
    </FormContainer>
  );
});

export default NmiCollectForm;
